'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import WorldwayNav from '@/components/WorldwayNav';

// ============================================================
// TYPES
// ============================================================

interface ValidationResult {
  test: string;
  status: 'pass' | 'fail' | 'pending' | 'skipped';
  detail: string;
  latencyMs?: number;
}

interface SyncStatus {
  tours: number;
  departures: number;
}

interface ReportData {
  configured: boolean;
  authStatus: 'authenticated' | 'failed' | 'not_configured';
  apiConnectivity: 'connected' | 'failed' | 'not_configured';
  webhookStatus: 'configured' | 'not_configured';
  productionMode: boolean;
  syncStatus: SyncStatus;
  validations: ValidationResult[];
  supplierRegistryStatus: 'live' | 'awaiting_credentials';
  credentials: {
    agency_code: boolean;
    secret_key: boolean;
    publishable_key: boolean;
    webhook_key: boolean;
  };
}

// ============================================================
// STATUS BADGE
// ============================================================

function StatusBadge({ status, label }: { status: string; label?: string }) {
  const map: Record<string, string> = {
    pass: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    authenticated: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    connected: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    live: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    configured: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    fail: 'bg-red-500/15 text-red-400 border-red-500/30',
    failed: 'bg-red-500/15 text-red-400 border-red-500/30',
    pending: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
    awaiting_credentials: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
    not_configured: 'bg-surface-4 text-muted-foreground border-border',
    skipped: 'bg-surface-4 text-muted-foreground border-border',
  };
  const cls = map[status] ?? 'bg-surface-4 text-muted-foreground border-border';
  const icons: Record<string, string> = { pass: '✓', authenticated: '✓', connected: '✓', live: '●', configured: '✓', fail: '✗', failed: '✗', pending: '◌', awaiting_credentials: '◌', not_configured: '–', skipped: '–' };
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border ${cls}`}>
      <span>{icons[status] ?? '?'}</span>
      {label ?? status.replace(/_/g, ' ')}
    </span>
  );
}

// ============================================================
// MAIN REPORT
// ============================================================

export default function GAdventuresIntegrationReportContent() {
  const [report, setReport] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [runningValidation, setRunningValidation] = useState(false);
  const [runningSync, setRunningSync] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchReport = async () => {
    setLoading(true);
    try {
      const [healthRes, syncRes] = await Promise.all([
        fetch('/api/gadventures/health'),
        fetch('/api/gadventures/sync'),
      ]);
      const health = await healthRes.json();
      const sync = await syncRes.json();

      const configured = health.status !== 'not_configured';
      const connected = health.status === 'healthy';

      const validations: ValidationResult[] = [
        {
          test: 'Agency Code Present',
          status: health.credentials?.agency_code ? 'pass' : 'fail',
          detail: health.credentials?.agency_code ? 'G_ADVENTURES_AGENCY_CODE is configured' : 'G_ADVENTURES_AGENCY_CODE is missing',
        },
        {
          test: 'Production Secret Key',
          status: health.credentials?.secret_key ? 'pass' : 'fail',
          detail: health.credentials?.secret_key ? 'G_ADVENTURES_SECRET_KEY is configured' : 'G_ADVENTURES_SECRET_KEY is missing',
        },
        {
          test: 'Production Publishable Key',
          status: health.credentials?.publishable_key ? 'pass' : 'fail',
          detail: health.credentials?.publishable_key ? 'G_ADVENTURES_PUBLISHABLE_KEY is configured' : 'G_ADVENTURES_PUBLISHABLE_KEY is missing',
        },
        {
          test: 'Webhook Validation Key',
          status: health.credentials?.webhook_key ? 'pass' : 'skipped',
          detail: health.credentials?.webhook_key ? 'G_ADVENTURES_WEBHOOK_KEY is configured' : 'Optional — webhook signature validation disabled',
        },
        {
          test: 'API Connectivity',
          status: connected ? 'pass' : configured ? 'fail' : 'skipped',
          detail: connected ? `Connected to G Adventures REST API (${health.latencyMs}ms)` : health.message ?? 'Not tested',
          latencyMs: health.latencyMs,
        },
        {
          test: 'Catalogue Sync',
          status: (sync.catalogue?.tours ?? 0) > 0 ? 'pass' : 'pending',
          detail: (sync.catalogue?.tours ?? 0) > 0
            ? `${sync.catalogue.tours} tours and ${sync.catalogue.departures} departures imported`
            : 'Run a full sync to import the G Adventures catalogue',
        },
        {
          test: 'Webhook Endpoint',
          status: 'pass',
          detail: 'POST /api/gadventures/webhooks is active and ready to receive events',
        },
        {
          test: 'Booking Engine',
          status: configured ? 'pass' : 'pending',
          detail: configured ? 'POST /api/gadventures/bookings is active' : 'Requires credentials to activate',
        },
        {
          test: 'Tour Pages',
          status: 'pass',
          detail: '/tours/g-adventures is live with search, filter, and booking',
        },
        {
          test: 'Production Mode',
          status: configured ? 'pass' : 'pending',
          detail: configured ? 'Production credentials active — live API calls enabled' : 'Awaiting production credentials',
        },
      ];

      setReport({
        configured,
        authStatus: connected ? 'authenticated' : configured ? 'failed' : 'not_configured',
        apiConnectivity: connected ? 'connected' : configured ? 'failed' : 'not_configured',
        webhookStatus: health.credentials?.webhook_key ? 'configured' : 'not_configured',
        productionMode: configured,
        syncStatus: sync.catalogue ?? { tours: 0, departures: 0 },
        validations,
        supplierRegistryStatus: configured && connected ? 'live' : 'awaiting_credentials',
        credentials: health.credentials ?? { agency_code: false, secret_key: false, publishable_key: false, webhook_key: false },
      });
    } catch {
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReport(); }, []);

  const runValidation = async () => {
    setRunningValidation(true);
    try {
      await fetch('/api/gadventures/credentials', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'validate' }),
      });
      await fetchReport();
    } finally {
      setRunningValidation(false);
    }
  };

  const runSync = async () => {
    setRunningSync(true);
    setSyncResult(null);
    try {
      const res = await fetch('/api/gadventures/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'full', triggeredBy: 'report_page' }),
      });
      const data = await res.json();
      setSyncResult(data.success
        ? `✓ Sync completed — ${data.result?.toursImported ?? 0} tours, ${data.result?.departuresImported ?? 0} departures imported`
        : `✗ Sync failed: ${data.error ?? 'Unknown error'}`);
      await fetchReport();
    } finally {
      setRunningSync(false);
    }
  };

  const TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'validation', label: 'Validation Tests' },
    { id: 'catalogue', label: 'Catalogue' },
    { id: 'integration', label: 'Integration Map' },
    { id: 'api', label: 'API Reference' },
  ];

  const passCount = report?.validations.filter((v) => v.status === 'pass').length ?? 0;
  const totalTests = report?.validations.length ?? 0;
  const readinessScore = totalTests > 0 ? Math.round((passCount / totalTests) * 100) : 0;

  return (
    <div className="min-h-screen bg-[var(--background)] text-[var(--foreground)]">
      <WorldwayNav />

      {/* Header */}
      <div className="pt-20 pb-8 border-b border-[var(--border)] bg-[var(--surface-2)]">
        <div className="max-w-6xl mx-auto px-6">
          <nav className="flex items-center gap-2 text-xs text-[var(--muted-foreground)] mb-4">
            <Link href="/" className="hover:text-[var(--primary)] transition-colors">Home</Link>
            <span>/</span>
            <Link href="/enterprise-admin-platform" className="hover:text-[var(--primary)] transition-colors">Enterprise Admin</Link>
            <span>/</span>
            <span className="text-[var(--foreground)]">G Adventures Integration Report</span>
          </nav>
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-[rgba(201,168,76,0.15)] border border-[rgba(201,168,76,0.2)] flex items-center justify-center text-xl">🌍</div>
                <div>
                  <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-serif)' }}>G Adventures Integration Report</h1>
                  <p className="text-xs text-[var(--muted-foreground)]">Enterprise Connector Activation Status · {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                </div>
              </div>
              {report && (
                <div className="flex items-center gap-2 flex-wrap mt-2">
                  <StatusBadge status={report.supplierRegistryStatus} label={report.supplierRegistryStatus === 'live' ? 'Supplier Registry: Live' : 'Supplier Registry: Awaiting Credentials'} />
                  <StatusBadge status={report.authStatus} label={`Auth: ${report.authStatus.replace(/_/g, ' ')}`} />
                  <StatusBadge status={report.apiConnectivity} label={`API: ${report.apiConnectivity.replace(/_/g, ' ')}`} />
                  <StatusBadge status={report.webhookStatus} label={`Webhooks: ${report.webhookStatus.replace(/_/g, ' ')}`} />
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button onClick={runValidation} disabled={runningValidation} className="px-4 py-2 rounded-lg border border-[var(--border)] text-xs font-medium hover:border-[var(--primary)]/50 transition-colors disabled:opacity-50">
                {runningValidation ? 'Validating...' : '🔍 Run Validation'}
              </button>
              <button onClick={runSync} disabled={runningSync} className="btn-primary px-4 py-2 rounded-lg text-xs font-semibold disabled:opacity-50">
                {runningSync ? 'Syncing...' : '🔄 Sync Catalogue'}
              </button>
            </div>
          </div>
          {syncResult && (
            <div className={`mt-3 text-xs px-3 py-2 rounded-lg ${syncResult.startsWith('✓') ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
              {syncResult}
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-[var(--border)] bg-[var(--surface-2)]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex gap-1 overflow-x-auto">
            {TABS.map((tab) => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-4 py-3 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === tab.id ? 'border-[var(--primary)] text-[var(--primary)]' : 'border-transparent text-[var(--muted-foreground)] hover:text-[var(--foreground)]'}`}>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => <div key={i} className="glass-card rounded-xl h-24 animate-pulse" />)}
          </div>
        ) : !report ? (
          <div className="text-center py-16 text-[var(--muted-foreground)]">Failed to load report data.</div>
        ) : (
          <>
            {/* OVERVIEW TAB */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* KPI Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: 'Production Readiness', value: `${readinessScore}%`, sub: `${passCount}/${totalTests} tests passed`, color: readinessScore >= 80 ? 'text-emerald-400' : readinessScore >= 50 ? 'text-amber-400' : 'text-red-400' },
                    { label: 'Tours in Catalogue', value: report.syncStatus.tours.toLocaleString(), sub: 'Imported from G Adventures', color: 'text-[var(--primary)]' },
                    { label: 'Departures', value: report.syncStatus.departures.toLocaleString(), sub: 'Upcoming departures synced', color: 'text-blue-400' },
                    { label: 'Supplier Status', value: report.supplierRegistryStatus === 'live' ? 'Live' : 'Pending', sub: report.supplierRegistryStatus === 'live' ? 'All systems operational' : 'Awaiting credentials', color: report.supplierRegistryStatus === 'live' ? 'text-emerald-400' : 'text-amber-400' },
                  ].map((kpi) => (
                    <div key={kpi.label} className="glass-card rounded-xl p-4">
                      <p className="text-xs text-[var(--muted-foreground)] mb-1">{kpi.label}</p>
                      <p className={`text-2xl font-bold ${kpi.color}`}>{kpi.value}</p>
                      <p className="text-[10px] text-[var(--muted-foreground)] mt-0.5">{kpi.sub}</p>
                    </div>
                  ))}
                </div>

                {/* Credential Status */}
                <div className="glass-card rounded-xl p-5">
                  <h3 className="text-sm font-semibold mb-4">Credential Configuration</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { label: 'Agency Code', key: 'agency_code', env: 'G_ADVENTURES_AGENCY_CODE' },
                      { label: 'Production Secret Key', key: 'secret_key', env: 'G_ADVENTURES_SECRET_KEY' },
                      { label: 'Production Publishable Key', key: 'publishable_key', env: 'G_ADVENTURES_PUBLISHABLE_KEY' },
                      { label: 'Webhook Validation Key', key: 'webhook_key', env: 'G_ADVENTURES_WEBHOOK_KEY' },
                    ].map((cred) => {
                      const isSet = report.credentials[cred.key as keyof typeof report.credentials];
                      return (
                        <div key={cred.key} className={`p-3 rounded-lg border ${isSet ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-amber-500/30 bg-amber-500/5'}`}>
                          <div className="flex items-center gap-1.5 mb-1">
                            <span className={`text-sm ${isSet ? 'text-emerald-400' : 'text-amber-400'}`}>{isSet ? '✓' : '○'}</span>
                            <p className="text-xs font-medium">{cred.label}</p>
                          </div>
                          <p className="text-[10px] text-[var(--muted-foreground)] font-mono">{cred.env}</p>
                          <p className={`text-[10px] mt-0.5 ${isSet ? 'text-emerald-400' : 'text-amber-400'}`}>{isSet ? 'Configured' : 'Missing'}</p>
                        </div>
                      );
                    })}
                  </div>
                  {!report.configured && (
                    <div className="mt-4 p-3 rounded-lg bg-amber-500/8 border border-amber-500/20">
                      <p className="text-xs text-amber-400">
                        <strong>To activate:</strong> Add the following environment variables to your .env file:
                        <br />
                        <code className="font-mono">G_ADVENTURES_AGENCY_CODE=your_agency_code</code><br />
                        <code className="font-mono">G_ADVENTURES_SECRET_KEY=your_production_secret_key</code><br />
                        <code className="font-mono">G_ADVENTURES_PUBLISHABLE_KEY=your_production_publishable_key</code><br />
                        <code className="font-mono">G_ADVENTURES_WEBHOOK_KEY=your_webhook_validation_key</code>
                      </p>
                    </div>
                  )}
                </div>

                {/* Quick Links */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { title: 'G Adventures Tours', desc: 'Browse the live tour catalogue', href: '/tours/g-adventures', icon: '🌍' },
                    { title: 'API Credentials', desc: 'Manage connector credentials', href: '/api-credentials', icon: '🔑' },
                    { title: 'Supplier Registry', desc: 'View all supplier statuses', href: '/enterprise-admin-platform', icon: '🏢' },
                  ].map((link) => (
                    <Link key={link.title} href={link.href} className="glass-card rounded-xl p-4 hover:border-[rgba(201,168,76,0.3)] transition-all group">
                      <div className="text-2xl mb-2">{link.icon}</div>
                      <p className="text-sm font-semibold group-hover:text-[var(--primary)] transition-colors">{link.title}</p>
                      <p className="text-xs text-[var(--muted-foreground)] mt-0.5">{link.desc}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* VALIDATION TAB */}
            {activeTab === 'validation' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold">Validation Test Results</h3>
                  <span className={`text-xs font-medium px-3 py-1 rounded-full ${readinessScore >= 80 ? 'bg-emerald-500/15 text-emerald-400' : 'bg-amber-500/15 text-amber-400'}`}>
                    {passCount}/{totalTests} Passed · {readinessScore}% Ready
                  </span>
                </div>
                {report.validations.map((v, i) => (
                  <div key={i} className="glass-card rounded-xl p-4 flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <span className={`text-lg mt-0.5 ${v.status === 'pass' ? 'text-emerald-400' : v.status === 'fail' ? 'text-red-400' : 'text-amber-400'}`}>
                        {v.status === 'pass' ? '✓' : v.status === 'fail' ? '✗' : v.status === 'skipped' ? '–' : '◌'}
                      </span>
                      <div>
                        <p className="text-sm font-medium">{v.test}</p>
                        <p className="text-xs text-[var(--muted-foreground)] mt-0.5">{v.detail}</p>
                        {v.latencyMs && <p className="text-[10px] text-[var(--muted-foreground)] mt-0.5">Latency: {v.latencyMs}ms</p>}
                      </div>
                    </div>
                    <StatusBadge status={v.status} />
                  </div>
                ))}
              </div>
            )}

            {/* CATALOGUE TAB */}
            {activeTab === 'catalogue' && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: 'Tours Imported', value: report.syncStatus.tours, icon: '🗺️' },
                    { label: 'Departures', value: report.syncStatus.departures, icon: '📅' },
                    { label: 'Countries', value: '100+', icon: '🌍' },
                    { label: 'Trip Styles', value: '8', icon: '🎯' },
                  ].map((item) => (
                    <div key={item.label} className="glass-card rounded-xl p-4 text-center">
                      <div className="text-3xl mb-2">{item.icon}</div>
                      <p className="text-xl font-bold text-[var(--primary)]">{typeof item.value === 'number' ? item.value.toLocaleString() : item.value}</p>
                      <p className="text-xs text-[var(--muted-foreground)]">{item.label}</p>
                    </div>
                  ))}
                </div>

                <div className="glass-card rounded-xl p-5">
                  <h3 className="text-sm font-semibold mb-4">Catalogue Sync Controls</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg border border-[var(--border)]">
                      <p className="text-sm font-medium mb-1">Full Catalogue Sync</p>
                      <p className="text-xs text-[var(--muted-foreground)] mb-3">Import all G Adventures tours, destinations, itineraries, pricing, and departures.</p>
                      <button onClick={runSync} disabled={runningSync || !report.configured} className="btn-primary px-4 py-2 rounded-lg text-xs font-semibold disabled:opacity-50">
                        {runningSync ? 'Syncing...' : '🔄 Run Full Sync'}
                      </button>
                    </div>
                    <div className="p-4 rounded-lg border border-[var(--border)]">
                      <p className="text-sm font-medium mb-1">Incremental Sync</p>
                      <p className="text-xs text-[var(--muted-foreground)] mb-3">Update availability and pricing for upcoming departures (next 90 days).</p>
                      <button
                        onClick={async () => {
                          const res = await fetch('/api/gadventures/sync', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: 'incremental' }) });
                          const d = await res.json();
                          setSyncResult(d.success ? `✓ Incremental sync: ${d.result?.departuresImported ?? 0} departures updated` : `✗ ${d.error}`);
                        }}
                        disabled={!report.configured}
                        className="px-4 py-2 rounded-lg border border-[var(--border)] text-xs font-medium hover:border-[var(--primary)]/50 transition-colors disabled:opacity-50"
                      >
                        ⚡ Incremental Sync
                      </button>
                    </div>
                  </div>
                  {syncResult && (
                    <div className={`mt-3 text-xs px-3 py-2 rounded-lg ${syncResult.startsWith('✓') ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
                      {syncResult}
                    </div>
                  )}
                </div>

                <div className="glass-card rounded-xl p-5">
                  <h3 className="text-sm font-semibold mb-3">Supported Data Types</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {['Tours', 'Destinations', 'Countries', 'Itineraries', 'Pricing', 'Availability', 'Images', 'Inclusions', 'Exclusions', 'Trip Styles', 'Physical Grading', 'Accommodation', 'Transport', 'Departures', 'Booking Rules'].map((item) => (
                      <div key={item} className="flex items-center gap-2 text-xs text-[var(--muted-foreground)]">
                        <span className="text-emerald-400">✓</span>{item}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* INTEGRATION MAP TAB */}
            {activeTab === 'integration' && (
              <div className="space-y-4">
                <h3 className="text-sm font-semibold">Platform Integration Map</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { module: 'AI Concierge', status: 'integrated', detail: 'G Adventures tours available for AI recommendations and itinerary building', icon: '🤖' },
                    { module: 'Booking Engine', status: 'integrated', detail: 'POST /api/gadventures/bookings — single checkout with Razorpay payment', icon: '📋' },
                    { module: 'Customer Portal', status: 'integrated', detail: 'G Adventures bookings appear in My Bookings with timeline and status sync', icon: '👤' },
                    { module: 'Enterprise Admin', status: 'integrated', detail: 'Supplier Registry, sync controls, and analytics in Enterprise Admin Platform', icon: '🏢' },
                    { module: 'Supplier Registry', status: report.supplierRegistryStatus === 'live' ? 'live' : 'awaiting_credentials', detail: `Status: ${report.supplierRegistryStatus === 'live' ? 'Live — all validation tests passed' : 'Awaiting production credentials'}`, icon: '📊' },
                    { module: 'Analytics', status: 'integrated', detail: 'Tour views, bookings, and revenue tracked in booking analytics', icon: '📈' },
                    { module: 'Dynamic Packages', status: 'integrated', detail: 'G Adventures tours available as package components in Trip Builder', icon: '✨' },
                    { module: 'Webhook Processing', status: report.webhookStatus === 'configured' ? 'integrated' : 'partial', detail: 'POST /api/gadventures/webhooks — booking and availability events', icon: '🔔' },
                    { module: 'Incremental Sync', status: 'integrated', detail: 'Automatic availability and pricing updates via /api/gadventures/sync', icon: '🔄' },
                    { module: 'Tour Pages', status: 'integrated', detail: '/tours/g-adventures — live search, filter, availability, and booking', icon: '🌍' },
                  ].map((item) => (
                    <div key={item.module} className="glass-card rounded-xl p-4 flex items-start gap-3">
                      <span className="text-2xl">{item.icon}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <p className="text-sm font-medium">{item.module}</p>
                          <StatusBadge status={item.status === 'integrated' ? 'pass' : item.status === 'live' ? 'pass' : item.status === 'partial' ? 'pending' : 'pending'} label={item.status} />
                        </div>
                        <p className="text-xs text-[var(--muted-foreground)]">{item.detail}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* API REFERENCE TAB */}
            {activeTab === 'api' && (
              <div className="space-y-4">
                <h3 className="text-sm font-semibold">G Adventures API Endpoints</h3>
                <div className="space-y-2">
                  {[
                    { method: 'GET', path: '/api/gadventures/health', desc: 'Connector health check and credential status' },
                    { method: 'GET', path: '/api/gadventures/credentials', desc: 'Get credential configuration status' },
                    { method: 'POST', path: '/api/gadventures/credentials', desc: 'Validate credentials (action: validate)' },
                    { method: 'GET', path: '/api/gadventures/sync', desc: 'Get sync status and catalogue counts' },
                    { method: 'POST', path: '/api/gadventures/sync', desc: 'Trigger full or incremental catalogue sync' },
                    { method: 'GET', path: '/api/gadventures/tours', desc: 'Search and browse G Adventures tours' },
                    { method: 'GET', path: '/api/gadventures/tours/[id]', desc: 'Get tour detail with departures' },
                    { method: 'POST', path: '/api/gadventures/bookings', desc: 'Create a G Adventures booking' },
                    { method: 'POST', path: '/api/gadventures/webhooks', desc: 'Receive G Adventures webhook events' },
                  ].map((ep) => (
                    <div key={ep.path} className="glass-card rounded-xl p-3 flex items-center gap-3">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded font-mono ${ep.method === 'GET' ? 'bg-blue-500/15 text-blue-400' : 'bg-emerald-500/15 text-emerald-400'}`}>{ep.method}</span>
                      <code className="text-xs font-mono text-[var(--primary)] flex-1">{ep.path}</code>
                      <span className="text-xs text-[var(--muted-foreground)] hidden md:block">{ep.desc}</span>
                    </div>
                  ))}
                </div>

                <div className="glass-card rounded-xl p-5 mt-4">
                  <h4 className="text-sm font-semibold mb-3">Environment Variables Required</h4>
                  <div className="space-y-2">
                    {[
                      { key: 'G_ADVENTURES_AGENCY_CODE', desc: 'Your G Adventures agency code', required: true },
                      { key: 'G_ADVENTURES_SECRET_KEY', desc: 'Production secret key (server-side only)', required: true },
                      { key: 'G_ADVENTURES_PUBLISHABLE_KEY', desc: 'Production publishable key', required: true },
                      { key: 'G_ADVENTURES_WEBHOOK_KEY', desc: 'Webhook HMAC validation key', required: false },
                    ].map((env) => (
                      <div key={env.key} className="flex items-center gap-3 text-xs">
                        <code className="font-mono text-[var(--primary)] flex-1">{env.key}</code>
                        <span className="text-[var(--muted-foreground)]">{env.desc}</span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] ${env.required ? 'bg-red-500/15 text-red-400' : 'bg-surface-3 text-muted-foreground'}`}>{env.required ? 'Required' : 'Optional'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
